import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { FieldHeader } from '@/components/atoms/FieldHeader/FieldHeader';
import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useBrains from '@/hooks/useBrains';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { Brain } from '@/shared/types/brain';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { CLEAR_DIRTY_VALUES, KEEP_DIRTY_VALUES } from '@/util/forms/resetOptions';
import { LENGTH_XS } from '@/util/validation/constants';

import { ResponseLength, ToneOfVoice } from '../../models';

const TONE_OF_VOICES: ToneOfVoice[] = ['creative', 'neutral', 'matter_of_fact', 'professional'];
const RESPONSE_SIZES: ResponseLength[] = ['short', 'medium', 'long'];
const FORM_ID = 'basic-settings-form';

interface BasicSettingsForm {
  name: string;
  tone_of_voice: number;
  response_size: ResponseLength;
}

interface BasicSettingsFieldsetProps {
  brain: Brain;
}

export const BasicSettingsFieldset = ({ brain }: BasicSettingsFieldsetProps) => {
  const { t } = useTranslation();
  const { updateBrainAsync } = useBrains(brain.brain_id);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );

  const form = useForm<BasicSettingsForm>({
    mode: 'onTouched',
    resetOptions: KEEP_DIRTY_VALUES,
    values: {
      name: brain?.guidelines?.name ?? '',
      tone_of_voice: brain?.guidelines?.tone_of_voice ?? 0,
      response_size: brain?.guidelines?.response_size ?? 'medium',
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = form;
  useTrackFormState({ isDirty, formId: FORM_ID });

  const onSubmit = async (data: BasicSettingsForm) => {
    await updateBrainAsync({
      brain_id: brain.brain_id,
      guidelines: {
        ...brain.guidelines,
        name: data.name || undefined,
        tone_of_voice: data.tone_of_voice,
        response_size: data.response_size,
      },
    });
    form.reset(data, CLEAR_DIRTY_VALUES);
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
        <FieldsetCard
          isDirty={isDirty}
          isLoading={isSubmitting}
          disabled={!isDirty || !canWrite}
          saveButtonLabel={t('common.save')}
          formId={FORM_ID}
        >
          <div className="flex flex-col gap-6">
            {/* Company Name */}
            <FormField
              name="name"
              control={control}
              rules={{
                maxLength: {
                  value: LENGTH_XS,
                  message: t('validation.max_length', { 0: LENGTH_XS }),
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('ai_agents.company_name.title')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('ai_agents.company_name.placeholder')}
                      disabled={!canWrite}
                    />
                  </FormControl>
                  <FormDescription>{t('ai_agents.company_name.subtitle')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Tone of Voice */}
            <div className="flex flex-col gap-2">
              <FieldHeader
                title={t('ai_agents.tone_of_voice.title')}
                tooltip={t('ai_agents.tone_of_voice.subtitle')}
              />
              <Controller
                name="tone_of_voice"
                control={control}
                render={({ field }) => (
                  <div className="flex gap-2">
                    {TONE_OF_VOICES.map((tone, index) => (
                      <InfoTooltip
                        key={tone}
                        content={t(`ai_agents.tone_of_voice.${tone}_tooltip`)}
                        side="bottom"
                        size="large"
                      >
                        <Button
                          type="button"
                          variant="outline-neutral"
                          size="sm"
                          aria-pressed={field.value === index - 1}
                          className={cn(
                            field.value === index - 1 &&
                              'border-informational bg-bg-info-soft text-informational'
                          )}
                          onClick={(e) => {
                            e.preventDefault();
                            field.onChange(index - 1);
                          }}
                          disabled={!canWrite}
                        >
                          {t(`ai_agents.tone_of_voice.${tone}`)}
                        </Button>
                      </InfoTooltip>
                    ))}
                  </div>
                )}
              />
            </div>

            {/* Response Length */}
            <div className="flex flex-col gap-2">
              <FieldHeader
                title={t('ai_agents.response_length.title')}
                tooltip={t('ai_agents.response_length.subtitle')}
              />
              <Controller
                name="response_size"
                control={control}
                render={({ field }) => (
                  <div className="flex gap-2">
                    {RESPONSE_SIZES.map((size) => (
                      <InfoTooltip
                        key={size}
                        content={t(`ai_agents.response_length.${size}_tooltip`)}
                        side="bottom"
                        size="large"
                      >
                        <Button
                          type="button"
                          variant="outline-neutral"
                          size="sm"
                          aria-pressed={field.value === size}
                          className={cn(
                            field.value === size &&
                              'border-informational bg-bg-info-soft text-informational'
                          )}
                          onClick={(e) => {
                            e.preventDefault();
                            field.onChange(size);
                          }}
                          disabled={!canWrite}
                        >
                          {t(`ai_agents.response_length.${size}`)}
                        </Button>
                      </InfoTooltip>
                    ))}
                  </div>
                )}
              />
            </div>
          </div>
        </FieldsetCard>
      </form>
    </Form>
  );
};
